// features/customer/components/CampaignsAndEvents.tsx
import React, { useState, useEffect, useRef } from "react";
import { API_ORIGIN, API_ROOT_URL, API_V1_BASE_URL } from "../../config/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function useDebouncedValue<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const isValidUUID = (value: unknown): value is string =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const authenticatedJsonHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("accessToken") || ""}`,
});

// Styled replacement for window.confirm — renders inside react-toastify
// instead of the native browser dialog.
const confirmToast = (message: string): Promise<boolean> => {
  return new Promise((resolve) => {
    toast(
      ({ closeToast }) => (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-800">{message}</p>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                closeToast?.();
                resolve(false);
              }}
              className="px-3 py-1.5 text-sm rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                closeToast?.();
                resolve(true);
              }}
              className="px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        closeButton: false,
        draggable: false,
        position: "top-center",
      },
    );
  });
};

const getLocalToday = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const isDateNotInPast = (dateStr: string): boolean => {
  if (!dateStr) return true;
  return dateStr >= getLocalToday();
};

// Backend may return 'ACTIVE' / 'Active' / 'ARCHIVED' etc. Normalise for the UI.
const normalizeStatus = (s: unknown): string => {
  const raw = String(s ?? "").trim();
  const v = raw.toUpperCase();
  if (v === "ACTIVE") return "Active";
  if (v === "ARCHIVED") return "Archived";
  if (v === "SCHEDULED") return "Scheduled";
  return raw;
};

interface CampaignAsset {
  id: string;
  url: string;
  type: "image" | "video" | "document";
  title: string;
  order: number;
}

interface Campaign {
  id: string;
  title: string;
  description?: string;
  startDate: string;
  endDate: string;
  notes: string;
  tags: string[];
  status: "Active" | "Archived";
  image: string;
  video?: string;
  // Legacy fields kept for compatibility
  message?: string;
  externalReference?: string;
  versionNumber?: number;
  buttonText?: string;
  buttonLink?: string;
  location?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
  approvedBy?: string;
  approvalStatus?: string;
  approvedAt?: string;
  isVisible?: boolean;
  order?: number;
  assets?: CampaignAsset[];
  eventIds?: string[]; // NEW — linked event UUIDs
  assetIds?: string[]; // NEW — linked asset IDs
}

interface Product {
  id: string;
  name: string;
  description?: string;
  launchDate: string;
  endDate?: string;
  notes: string;
  tags: string[];
  status: "Active" | "Archived";
  image: string;
  video?: string;
  assetIds?: string[]; // NEW — linked asset IDs
}

interface Content {
  id: string;
  name: string;
  dateOfCreation: string;
  notes: string;
  tags: string[];
  status: "Active" | "Archived";
  image: string;
  video?: string;
}

interface Event {
  id: string;
  eventName: string;
  description?: string;
  eventDate: string;
  endDate?: string;
  notes: string;
  tags: string[];
  status: "Active" | "Archived" | "Scheduled";
  image: string;
  video?: string;
}

interface Asset {
  id: string; // bigint from Postgres comes back as string
  original_filename: string;
  source_key: string;
  master_key?: string | null;
  proxy_key?: string | null;
  checksum?: string | null;
  status:
    | "received"
    | "validating"
    | "rejected"
    | "queued"
    | "transcoding"
    | "completed"
    | "failed";
  codec?: string | null;
  container?: string | null;
  width?: number | null;
  height?: number | null;
  duration_sec?: number | null;
  file_size_bytes?: number | null;
  created_at?: string;
  updated_at?: string;
  product_id?: string | null;
}

interface ArchiveCounts {
  campaigns: number;
  products: number;
  contents: number;
  events: number;
  total: number;
}

type ArchiveVisibility = "Public" | "Private";

type CampaignsView =
  | "summary"
  | "campaigns"
  | "products"
  | "contents"
  | "events";

interface CampaignsAndEventsProps {
  tenant?: any;
  view?: CampaignsView; // when set, locks the component to one section and hides the internal tab switcher
}

interface EventMultiSelectProps {
  events: Event[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
}

const EventMultiSelect: React.FC<EventMultiSelectProps> = ({
  events,
  selectedIds,
  onToggle,
  onRemove,
  isOpen,
  setIsOpen,
  searchQuery,
  setSearchQuery,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [setIsOpen]);

  const filteredEvents = events.filter((ev) =>
    ev.eventName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="relative" ref={containerRef}>
      {/* Chip field / trigger */}
      <div
        className="w-full min-h-[42px] p-2 border border-gray-300 rounded-lg bg-white cursor-text flex flex-wrap gap-2 items-center focus-within:ring-2 focus-within:ring-purple-500"
        onClick={() => setIsOpen(true)}
      >
        {selectedIds.length === 0 && (
          <span className="text-gray-400 text-sm px-1">Select events...</span>
        )}
        {selectedIds.map((id) => {
          const ev = events.find((e) => e.id === id);
          if (!ev) return null;
          return (
            <span
              key={id}
              className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-sm rounded-full"
            >
              {ev.eventName}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(id);
                }}
                className="text-purple-400 hover:text-purple-700"
              >
                ×
              </button>
            </span>
          );
        })}
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 flex flex-col">
          <div className="p-2 border-b border-gray-200 relative">
            <input
              type="text"
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search events..."
              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 text-sm"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <div className="overflow-y-auto">
            {filteredEvents.length === 0 ? (
              <p className="text-sm text-gray-400 p-3">No events found</p>
            ) : (
              filteredEvents.map((ev) => (
                <label
                  key={ev.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(ev.id)}
                    onChange={() => onToggle(ev.id)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-gray-700">{ev.eventName}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface AssetChipFieldProps {
  assets: Asset[];
  selectedIds: string[];
  onRemove: (id: string) => void;
  onOpenPicker: () => void;
}

const AssetChipField: React.FC<AssetChipFieldProps> = ({
  assets,
  selectedIds,
  onRemove,
  onOpenPicker,
}) => {
  return (
    <div
      className="w-full min-h-[42px] p-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex flex-wrap gap-2 items-center hover:border-purple-400 transition"
      onClick={onOpenPicker}
    >
      {selectedIds.length === 0 && (
        <span className="text-gray-400 text-sm px-1">
          Click to select assets...
        </span>
      )}
      {selectedIds.map((id) => {
        const asset = assets.find((a) => a.id === id);
        if (!asset) return null;
        return (
          <span
            key={id}
            className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-sm rounded-full"
          >
            {asset.original_filename}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(id);
              }}
              className="text-purple-400 hover:text-purple-700"
            >
              ×
            </button>
          </span>
        );
      })}
    </div>
  );
};

interface AssetPickerModalProps {
  assets: Asset[];
  assetsLoading: boolean;
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClose: () => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
}

const AssetPickerModal: React.FC<AssetPickerModalProps> = ({
  assets,
  assetsLoading,
  selectedIds,
  onToggle,
  onClose,
  searchQuery,
  setSearchQuery,
}) => {
  const term = searchQuery.toLowerCase();
  const filtered = assets.filter((a) =>
    a.original_filename.toLowerCase().includes(term),
  );
  // Selected assets float to the top so the user sees their current picks first
  const sorted = [...filtered].sort((a, b) => {
    const aSel = selectedIds.includes(a.id) ? 0 : 1;
    const bSel = selectedIds.includes(b.id) ? 0 : 1;
    return aSel - bSel;
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-semibold text-gray-800">
                Select Assets
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                Choose one or more assets to link to this campaign
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-black hover:text-black text-4xl font-bold leading-none"
            >
              ×
            </button>
          </div>
          <div className="relative mt-3">
            <input
              type="text"
              autoFocus
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search assets..."
              className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 text-sm"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {assetsLoading ? (
            <p className="text-sm text-gray-400 text-center py-8">
              Loading assets...
            </p>
          ) : sorted.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8 px-6">
              {searchQuery
                ? "No assets match your search"
                : "No assets available. Only assets linked to a product can be added to a campaign — link the asset to a product first."}
            </p>
          ) : (
            <div className="space-y-1">
              {sorted.map((asset) => (
                <label
                  key={asset.id}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer border border-transparent hover:bg-gray-50 hover:border-gray-200"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(asset.id)}
                    onChange={() => onToggle(asset.id)}
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {asset.original_filename}
                    </p>
                    <p className="text-xs text-gray-400">
                      {asset.status}
                      {asset.container ? ` • ${asset.container}` : ""}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <span className="text-sm text-gray-500">
            {selectedIds.length} selected
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

interface EventPickerModalProps {
  events: Event[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClose: () => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
}

const EventPickerModal: React.FC<EventPickerModalProps> = ({
  events,
  selectedIds,
  onToggle,
  onClose,
  searchQuery,
  setSearchQuery,
}) => {
  const filtered = events.filter((e) =>
    e.eventName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-xl max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Select Events</h2>
            <button
              onClick={onClose}
              className="text-black hover:text-black text-4xl font-bold leading-none"
            >
              ×
            </button>
          </div>

          <div className="relative mt-4">
            <input
              autoFocus
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search events..."
              className="w-full border rounded-lg px-3 py-2 pr-8"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {filtered.map((event) => (
            <label
              key={event.id}
              className="flex items-center gap-3 p-2 rounded hover:bg-gray-100 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(event.id)}
                onChange={() => onToggle(event.id)}
              />
              {event.eventName}
            </label>
          ))}
        </div>

        <div className="border-t p-4 flex justify-end">
          <button
            onClick={onClose}
            className="bg-purple-600 text-white px-5 py-2 rounded-lg"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

interface PaginationControlsProps {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

// Builds a compact page list like: 1, 2, 3, 4, 5, ..., 12
const buildPageList = (
  current: number,
  totalPages: number,
): (number | "...")[] => {
  const delta = 1;
  const range: (number | "...")[] = [];
  const start = Math.max(2, current - delta);
  const end = Math.min(totalPages - 1, current + delta);

  range.push(1);
  if (start > 2) range.push("...");
  for (let i = start; i <= end; i++) range.push(i);
  if (end < totalPages - 1) range.push("...");
  if (totalPages > 1) range.push(totalPages);

  return range;
};

const PaginationControls: React.FC<PaginationControlsProps> = ({
  page,
  totalPages,
  total,
  pageSize,
  loading,
  onPageChange,
  onPageSizeChange,
}) => {
  if (total === 0) return null;

  const pages = buildPageList(page, totalPages);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between px-2">
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <span>
          {loading
            ? "Loading…"
            : `Showing page ${page} of ${totalPages} (${total} total)`}
        </span>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="ml-2 border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-purple-500"
          disabled={loading}
        >
          <option value={4}>4</option>
          <option value={8}>8</option>
          <option value={12}>12</option>
          <option value={24}>24</option>
        </select>
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1 || loading}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition"
        >
          ← Prev
        </button>

        {pages.map((p, idx) =>
          p === "..." ? (
            <span
              key={`ellipsis-${idx}`}
              className="px-2 text-gray-400 select-none"
            >
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              disabled={loading}
              className={`min-w-[36px] px-3 py-1.5 text-sm rounded-lg border transition ${
                p === page
                  ? "bg-purple-600 text-white border-purple-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {p}
            </button>
          ),
        )}

        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages || loading}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition"
        >
          Next →
        </button>
      </div>
    </div>
  );
};

const CampaignsAndEvents: React.FC<CampaignsAndEventsProps> = ({
  tenant,
  view,
}) => {
  const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
  const tenantId = loggedInUser.tenantId;
  const userId = loggedInUser.id;

  // ===== STATE =====
  // Campaigns
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(
    null,
  );
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // ---- Server-side pagination: Campaigns ----
  const [campaignsPage, setCampaignsPage] = useState(1);
  const [campaignsPageSize, setCampaignsPageSize] = useState(8);
  const [campaignsTotal, setCampaignsTotal] = useState(0);
  const [campaignsTotalPages, setCampaignsTotalPages] = useState(1);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  // ---- Server-side pagination: Events ----
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsPageSize, setEventsPageSize] = useState(8);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsTotalPages, setEventsTotalPages] = useState(1);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Debounce the search box (400ms) so we don't hit the API on every keystroke
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Products
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showProductDetailsModal, setShowProductDetailsModal] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState<string | null>(null);
  const [productSearchInput, setProductSearchInput] = useState("");
  const debouncedProductSearch = useDebouncedValue(productSearchInput, 400);
  const [productStatusFilter, setProductStatusFilter] = useState("all");
  const [productPage, setProductPage] = useState(1);
  const [productLimit, setProductLimit] = useState(12);
  const [productTotal, setProductTotal] = useState(0);
  const [productTotalPages, setProductTotalPages] = useState(1);

  // Contents
  const [contents, setContents] = useState<Content[]>([]);
  const [selectedContent, setSelectedContent] = useState<Content | null>(null);
  const [showContentModal, setShowContentModal] = useState(false);
  const [showContentDetailsModal, setShowContentDetailsModal] = useState(false);

  // Events
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);

  // Archive visibility
  const [archiveVisibility, setArchiveVisibility] =
    useState<ArchiveVisibility>("Public");
  const [activeTab, setActiveTab] = useState<
    "campaigns" | "products" | "contents" | "events"
  >("campaigns");

  // When a fixed `view` is supplied (Customer Manager tabs), lock the internal tab to match it.
  useEffect(() => {
    if (view && view !== "summary") {
      setActiveTab(view as "campaigns" | "products" | "contents" | "events");
    }
  }, [view]);

  // Form states
  const [formData, setFormData] = useState<Partial<Campaign>>({
    title: "",
    description: "",
    startDate: getLocalToday(),
    endDate: "",
    notes: "",
    tags: [],
    status: "Active",
    image: "",
    video: "",
    eventIds: [], // NEW
    assetIds: [], // NEW
  });

  const [productFormData, setProductFormData] = useState<Partial<Product>>({
    name: "",
    description: "",
    launchDate: "",
    endDate: "",
    notes: "",
    tags: [],
    status: "Active",
    image: "",
    video: "",
    assetIds: [], // NEW
  });

  const [contentFormData, setContentFormData] = useState<Partial<Content>>({
    name: "",
    dateOfCreation: getLocalToday(),
    notes: "",
    tags: [],
    status: "Active",
    image: "",
    video: "",
  });

  const [eventFormData, setEventFormData] = useState<Partial<Event>>({
    eventName: "",
    description: "",
    eventDate: "",
    endDate: "",
    notes: "",
    tags: [],
    status: "Scheduled",
    image: "",
    video: "",
  });

  // UI helpers
  const [activeMediaTab, setActiveMediaTab] = useState<"image" | "video">(
    "image",
  );
  const [imagePreview, setImagePreview] = useState<string>("");
  const [videoPreview, setVideoPreview] = useState<string>("");
  const [newTag, setNewTag] = useState("");
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string>("");
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);
  const [eventSearchQuery, setEventSearchQuery] = useState("");
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [campaignAssets, setCampaignAssets] = useState<Asset[]>([]);
  const [campaignAssetsLoading, setCampaignAssetsLoading] = useState(false);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [showEventPicker, setShowEventPicker] = useState(false);
  const [assetSearchQuery, setAssetSearchQuery] = useState("");
  const [showProductAssetPicker, setShowProductAssetPicker] = useState(false);
  const [productAssetSearchQuery, setProductAssetSearchQuery] = useState("");

  const singleViewHeading: Record<string, { title: string; subtitle: string }> =
    {
      campaigns: {
        title: "Campaigns",
        subtitle: "Create and manage your marketing campaigns",
      },
      events: {
        title: "Events",
        subtitle: "Create and manage your events",
      },
      products: {
        title: "Products",
        subtitle: "Create and manage your product launches",
      },
    };

  // Refs
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const productImageInputRef = useRef<HTMLInputElement>(null);
  const contentImageInputRef = useRef<HTMLInputElement>(null);
  const eventImageInputRef = useRef<HTMLInputElement>(null);

  // ===== FORMAT HELPERS =====
  const formatDate = (date: string) => {
    if (!date) return "-";
    const datePart = date.split("T")[0]; // read calendar date directly, no timezone shift
    const [year, month, day] = datePart.split("-");
    if (!year || !month || !day) return "-";
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${day} ${months[parseInt(month, 10) - 1]} ${year}`;
  };

  // ===== DATA LOADING =====
  // Campaigns – API (from feature/campaign-ame)
  const loadCampaigns = async (
    page: number = campaignsPage,
    limit: number = campaignsPageSize,
  ) => {
    if (!tenantId) return;
    setCampaignsLoading(true);
    try {
      const params = new URLSearchParams({
        tenant_id: tenantId,
        page: String(page),
        limit: String(limit),
      });
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (debouncedSearchTerm) params.append("search", debouncedSearchTerm);

      const res = await fetch(
        `${API_V1_BASE_URL}/campaigns?${params.toString()}`,
      );
      const result = await res.json();
      if (result.success) {
        const mapped = result.data.map((c: any) => ({
          id: c.campaign_id,
          title: c.title,
          description: c.description,
          startDate: c.start_date,
          endDate: c.end_date,
          notes: c.notes,
          tags: c.tags,
          status: normalizeStatus(c.status) as Campaign["status"],
          image: c.image_url || "",
          video: c.video_url || "",
          eventIds: Array.isArray(c.event_ids) ? c.event_ids : [],
          assetIds: Array.isArray(c.asset_ids) ? c.asset_ids : [],
          createdBy: c.created_by,
          updatedBy: c.updated_by,
          createdAt: c.created_at,
          updatedAt: c.updated_at,
        }));
        setCampaigns(mapped);
        setCampaignsPage(result.page ?? page);
        setCampaignsTotal(result.total ?? mapped.length);
        setCampaignsTotalPages(
          result.totalPages ??
            Math.max(1, Math.ceil((result.total ?? mapped.length) / limit)),
        );
      }
    } catch (err) {
      console.error("Failed to load campaigns:", err);
    } finally {
      setCampaignsLoading(false);
    }
  };

  // Products – API (from HEAD)
  const loadProducts = async () => {
    if (!tenantId) {
      setProductsError("No tenant ID found — check that you're logged in.");
      return;
    }
    setProductsLoading(true);
    setProductsError(null);
    try {
      const params = new URLSearchParams({
        tenant_id: tenantId,
        page: String(productPage),
        limit: String(productLimit),
      });
      if (debouncedProductSearch) params.set("search", debouncedProductSearch);
      if (productStatusFilter !== "all")
        params.set("status", productStatusFilter);

      const res = await fetch(`${API_ROOT_URL}/products?${params.toString()}`);
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const result = await res.json();
      if (result.success) {
        setProducts(result.data.map(mapProductFromApi));
        if (result.pagination) {
          setProductTotal(result.pagination.total);
          setProductTotalPages(result.pagination.totalPages);
        }
      } else {
        setProductsError(result.message || "Failed to load products");
      }
    } catch (err) {
      console.error("Failed to load products", err);
      setProductsError("Could not load products. Please try again.");
    } finally {
      setProductsLoading(false);
    }
  };

  const mapProductFromApi = (p: any): Product => ({
    id: p.product_id,
    name: p.title,
    description: p.description,
    launchDate: p.start_date,
    endDate: p.end_date,
    notes: p.notes,
    tags: Array.isArray(p.tags)
      ? p.tags
      : typeof p.tags === "string"
        ? p.tags
            .replace(/[{}"]/g, "")
            .split("•")
            .map((t: string) => t.trim())
            .filter(Boolean)
        : [],
    status: normalizeStatus(p.status) as Product["status"],
    image: p.image_url || "",
    video: p.video_url || "",
  });

  // Contents – Mock
  const loadMockContents = () => {
    const mockContents: Content[] = [
      {
        id: "c1",
        name: "Summer Lookbook 2024",
        dateOfCreation: "2024-05-20",
        notes: "Digital catalog for summer styles",
        tags: ["lookbook", "catalog", "summer"],
        status: "Active",
        image:
          "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=500",
      },
    ];
    setContents(mockContents);
  };

  // Events – API (from HEAD)
  const loadEvents = async (
    page: number = eventsPage,
    limit: number = eventsPageSize,
  ) => {
    if (!tenantId) return;
    setEventsLoading(true);
    try {
      const params = new URLSearchParams({
        tenant_id: tenantId,
        page: String(page),
        limit: String(limit),
      });
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (debouncedSearchTerm) params.append("search", debouncedSearchTerm);

      // NOTE: events live under /events, NOT /api/v1
      const res = await fetch(`${API_ORIGIN}/events?${params.toString()}`);
      const result = await res.json();
      if (result.success) {
        const mapped = result.data.map((e: any) => ({
          id: e.event_id,
          eventName: e.title,
          description: e.description,
          eventDate: e.start_date ? e.start_date.split("T")[0] : "",
          endDate: e.end_date ? e.end_date.split("T")[0] : "",
          notes: e.notes,
          tags: Array.isArray(e.tags)
            ? e.tags
            : typeof e.tags === "string"
              ? e.tags
                  .replace(/[{}"]/g, "")
                  .split("•")
                  .map((t: string) => t.trim())
                  .filter(Boolean)
              : [],
          status: normalizeStatus(e.status) as Event["status"],
          image: e.image_url || "",
          video: e.video_url || "",
        }));
        setEvents(mapped);
        setEventsPage(result.page ?? page);
        setEventsTotal(result.total ?? mapped.length);
        setEventsTotalPages(
          result.totalPages ??
            Math.max(1, Math.ceil((result.total ?? mapped.length) / limit)),
        );
      }
    } catch (err) {
      console.error("Failed to load events", err);
    } finally {
      setEventsLoading(false);
    }
  };

  // Assets – Mock (backed by a real GET API today; swap the URL for the real assets endpoint later)
  const loadAssets = async () => {
    setAssetsLoading(true);
    try {
      const res = await fetch(
        `${API_ROOT_URL}/assets?limit=200&skip_preview=true`,
      );
      if (!res.ok) {
        throw new Error(`Request failed with ${res.status}`);
      }
      const result = await res.json();
      console.log("Assets API Response:", result);
      const assetList = result.data || result;
      const mappedAssets: Asset[] = assetList.map((asset: any) => ({
        id: String(asset.id),
        original_filename: asset.original_filename,
        source_key: asset.source_key,
        master_key: asset.master_key,
        proxy_key: asset.proxy_key,
        checksum: asset.checksum,
        status: asset.status,
        codec: asset.codec,
        container: asset.container,
        width: asset.width,
        height: asset.height,
        duration_sec: asset.duration_sec,
        file_size_bytes: asset.file_size_bytes,
        created_at: asset.created_at,
        updated_at: asset.updated_at,
        product_id: asset.product_id,
      }));
      setAssets(mappedAssets);
    } catch (err) {
      console.error("Failed to load assets", err);
    } finally {
      setAssetsLoading(false);
    }
  };

  const loadCampaignAssets = async () => {
    setCampaignAssetsLoading(true);
    try {
      const res = await fetch(
        `${API_ROOT_URL}/assets?only_product_mapped=true&limit=200&skip_preview=true`,
      );
      if (!res.ok) {
        throw new Error(`Request failed with ${res.status}`);
      }
      const result = await res.json();
      const assetList = result.data || result;
      const mappedAssets: Asset[] = assetList.map((asset: any) => ({
        id: String(asset.id),
        original_filename: asset.original_filename,
        source_key: asset.source_key,
        master_key: asset.master_key,
        proxy_key: asset.proxy_key,
        checksum: asset.checksum,
        status: asset.status,
        codec: asset.codec,
        container: asset.container,
        width: asset.width,
        height: asset.height,
        duration_sec: asset.duration_sec,
        file_size_bytes: asset.file_size_bytes,
        created_at: asset.created_at,
        updated_at: asset.updated_at,
        product_id: asset.product_id,
      }));
      setCampaignAssets(mappedAssets);
    } catch (err) {
      console.error("Failed to load campaign-eligible assets", err);
    } finally {
      setCampaignAssetsLoading(false);
    }
  };

  // Initial load — Products/Content/Assets aren't backend-paginated, so fetch once.
  // Campaigns/Events are fetched by the dedicated effects below (server-driven).
  useEffect(() => {
    loadMockContents();
    loadAssets();
    loadCampaignAssets();
    loadEvents(1, eventsPageSize);
  }, []);

  // Campaigns: refetch from the backend whenever the tab is active, or the
  // page/page-size/status/search changes. Pagination, filtering, and search
  // all happen server + DB side — nothing is sliced/filtered on the client.
  useEffect(() => {
    if (activeTab !== "campaigns") return;

    loadCampaigns(campaignsPage, campaignsPageSize);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenantId,
    activeTab,
    campaignsPage,
    campaignsPageSize,
    filterStatus,
    debouncedSearchTerm,
  ]);

  // Events: same pattern.
  useEffect(() => {
    if (activeTab !== "events") return;

    loadEvents(eventsPage, eventsPageSize);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tenantId,
    activeTab,
    eventsPage,
    eventsPageSize,
    filterStatus,
    debouncedSearchTerm,
  ]);

  // Reset to page 1 whenever the status filter or search text changes,
  // so the user isn't stranded on a page that no longer exists.
  useEffect(() => {
    setCampaignsPage(1);
    setEventsPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, debouncedSearchTerm]);

  // Product pagination effects
  useEffect(() => {
    setProductPage(1);
  }, [debouncedProductSearch, productStatusFilter, productLimit]);

  useEffect(() => {
    loadProducts();
  }, [
    tenantId,
    productPage,
    productLimit,
    debouncedProductSearch,
    productStatusFilter,
  ]);

  // ===== CAMPAIGN CRUD (API) =====
  const handleCreateCampaign = async () => {
    if (!formData.title) {
      toast.warning("Please fill in Title");
      return;
    }
    if (!isDateNotInPast(formData.startDate || "")) {
      toast.warning(
        "Start date cannot be in the past. Please pick today or a future date.",
      );
      return;
    }
    if (formData.endDate && !isDateNotInPast(formData.endDate)) {
      toast.warning(
        "End date cannot be in the past. Please pick today or a future date.",
      );
      return;
    }
    if (!tenantId || !userId) {
      toast.error("Login session expired. Please login again.");
      return;
    }

    setSaving(true);
    setApiError("");

    try {
      const payload = {
        title: formData.title || "",
        description: formData.description || "",
        notes: formData.notes || "",
        start_date: formData.startDate
          ? new Date(formData.startDate).toISOString()
          : new Date().toISOString(),
        end_date: formData.endDate
          ? new Date(formData.endDate).toISOString()
          : null,
        created_by: userId,
        updated_by: userId,
        status: formData.status || "Active",
        tenant_id: tenantId,
        image_url: formData.image || "",
        video_url: formData.video || "",
        tags: formData.tags || [],
      };

      const res = await fetch(`${API_V1_BASE_URL}/campaigns/create`, {
        method: "POST",
        headers: authenticatedJsonHeaders(),
        body: JSON.stringify(payload),
      });

      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || "Failed to create campaign");
      }

      const created = result.data;

      // Link selected events to the newly created campaign (correct base path — no /v1)
      if (formData.eventIds && formData.eventIds.length > 0) {
        const linkEventsResponse = await fetch(
          `${API_ROOT_URL}/campaign-events`,
          {
            method: "POST",
            headers: authenticatedJsonHeaders(),
            body: JSON.stringify({
              campaign_id: created.campaign_id,
              event_ids: formData.eventIds,
              created_by: userId,
            }),
          },
        );
        const linkEventsResult = await linkEventsResponse.json();
        if (!linkEventsResponse.ok || !linkEventsResult.success) {
          throw new Error(
            linkEventsResult.message || "Failed to link campaign events",
          );
        }
      }

      // Link selected assets to the newly created campaign
      if (formData.assetIds && formData.assetIds.length > 0) {
        const linkAssetsResponse = await fetch(
          `${API_ROOT_URL}/campaign-assets`,
          {
            method: "POST",
            headers: authenticatedJsonHeaders(),
            body: JSON.stringify({
              campaign_id: created.campaign_id,
              asset_ids: formData.assetIds,
              created_by: userId,
            }),
          },
        );
        const linkAssetsResult = await linkAssetsResponse.json();
        if (!linkAssetsResponse.ok || !linkAssetsResult.success) {
          throw new Error(
            linkAssetsResult.message || "Failed to link campaign assets",
          );
        }
      }

      const newCampaign: Campaign = {
        id: created.campaign_id,
        title: created.title,
        description: created.description,
        startDate: created.start_date,
        endDate: created.end_date,
        notes: created.notes,
        tags: Array.isArray(created.tags) ? created.tags : [],
        status: created.status,
        image: created.image_url || "",
        video: created.video_url || "",
        eventIds: formData.eventIds || [],
        assetIds: formData.assetIds || [],
        createdBy: created.created_by,
        updatedBy: created.updated_by,
        createdAt: created.created_at,
        updatedAt: created.updated_at,
      };

      setCampaigns([newCampaign, ...campaigns]); // instant feedback
      setCampaignsTotal((t) => t + 1); // tab count updates immediately
      setCampaignsPage(1);
      loadCampaigns(1, campaignsPageSize); // sync order/page size with the server
      setShowCreateModal(false);
      resetForm();
      toast.success("Campaign created successfully!");
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Failed to create campaign");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateCampaign = async () => {
    if (!selectedCampaign) return;
    if (!isDateNotInPast(formData.startDate || "")) {
      toast.warning(
        "Start date cannot be in the past. Please pick today or a future date.",
      );
      return;
    }
    if (formData.endDate && !isDateNotInPast(formData.endDate)) {
      toast.warning(
        "End date cannot be in the past. Please pick today or a future date.",
      );
      return;
    }
    if (!tenantId || !userId) {
      toast.error("Login session expired. Please login again.");
      return;
    }
    if (!isValidUUID(selectedCampaign.id)) {
      toast.error("Campaign ID is invalid.");
      return;
    }

    setSaving(true);
    setApiError("");

    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        notes: formData.notes,
        start_date: formData.startDate
          ? new Date(formData.startDate).toISOString()
          : formData.startDate,
        end_date: formData.endDate
          ? new Date(formData.endDate).toISOString()
          : null,
        updated_by: userId,
        status: formData.status,
        tenant_id: tenantId,
        image_url: formData.image,
        video_url: formData.video,
        tags: formData.tags,
      };

      const res = await fetch(
        `${API_V1_BASE_URL}/campaigns/update/${selectedCampaign.id}`,
        {
          method: "PUT",
          headers: authenticatedJsonHeaders(),
          body: JSON.stringify(payload),
        },
      );

      const result = await res.json();
      if (!res.ok || !result.success) {
        throw new Error(result.message || "Update failed");
      }

      const updated = result.data;

      // Sync linked events — also when the list is now empty (unlink all)
      const eventsChanged =
        JSON.stringify([...(selectedCampaign.eventIds || [])].sort()) !==
        JSON.stringify([...(formData.eventIds || [])].sort());
      if (eventsChanged) {
        const linkEventsResponse = await fetch(
          `${API_ROOT_URL}/campaign-events/${selectedCampaign.id}`,
          {
            method: "PUT",
            headers: authenticatedJsonHeaders(),
            body: JSON.stringify({
              event_ids: formData.eventIds,
              updated_by: userId,
            }),
          },
        );
        const linkEventsResult = await linkEventsResponse.json();
        if (!linkEventsResponse.ok || !linkEventsResult.success) {
          throw new Error(
            linkEventsResult.message || "Failed to update campaign events",
          );
        }
      }

      // Sanitize so a stray undefined/null can never reach the API
      const cleanCampaignAssetIds = (formData.assetIds || []).filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      );

      const assetsChanged =
        JSON.stringify([...(selectedCampaign.assetIds || [])].sort()) !==
        JSON.stringify([...cleanCampaignAssetIds].sort());
      if (assetsChanged) {
        const linkAssetsResponse = await fetch(
          `${API_ROOT_URL}/campaign-assets/${selectedCampaign.id}`,
          {
            method: "PUT",
            headers: authenticatedJsonHeaders(),
            body: JSON.stringify({
              asset_ids: cleanCampaignAssetIds,
              updated_by: userId,
            }),
          },
        );
        const linkAssetsResult = await linkAssetsResponse.json();
        if (!linkAssetsResponse.ok || !linkAssetsResult.success) {
          throw new Error(
            linkAssetsResult.message || "Failed to update campaign assets",
          );
        }
      }

      const updatedCampaign: Campaign = {
        id: updated.campaign_id,
        title: updated.title,
        description: updated.description,
        startDate: updated.start_date,
        endDate: updated.end_date,
        notes: updated.notes,
        tags: Array.isArray(updated.tags) ? updated.tags : [],
        status: normalizeStatus(updated.status) as Campaign["status"],
        image: updated.image_url || "",
        video: updated.video_url || "",
        eventIds: formData.eventIds || [],
        assetIds: cleanCampaignAssetIds,
        createdBy: updated.created_by,
        updatedBy: updated.updated_by,
        createdAt: updated.created_at,
        updatedAt: updated.updated_at,
      };

      setCampaigns((prev) =>
        prev.map((c) => (c.id === updatedCampaign.id ? updatedCampaign : c)),
      );
      setSelectedCampaign(null);
      setShowEditModal(false);
      resetForm();
      toast.success("Campaign updated successfully!");
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCampaign = async (campaign: Campaign) => {
    const confirmed = await confirmToast(
      `Are you sure you want to delete "${campaign.title}"?`,
    );
    if (!confirmed) return;

    try {
      const res = await fetch(
        `${API_V1_BASE_URL}/campaigns/${campaign.id}?tenant_id=${tenantId}`,
        {
          method: "DELETE",
          headers: authenticatedJsonHeaders(),
        },
      );
      const result = await res.json();

      if (res.status === 409) {
        toast.warning(
          result.message ||
            "This campaign is linked to one or more events/assets and cannot be deleted. Unlink them first.",
        );
        return;
      }

      if (!res.ok || !result.success) {
        throw new Error(result.message || "Failed to delete campaign");
      }
      toast.success("Campaign deleted successfully!");
      setShowEditModal(false);
      setShowDetailsModal(false);
      setSelectedCampaign(null);
      setCampaigns((prev) => prev.filter((c) => c.id !== campaign.id));
      setCampaignsTotal((t) => Math.max(0, t - 1));
      if (campaigns.length === 1 && campaignsPage > 1) {
        setCampaignsPage(campaignsPage - 1); // the page effect refetches
      } else {
        await loadCampaigns(campaignsPage, campaignsPageSize);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to delete campaign");
    }
  };

  // ===== PRODUCT CRUD (API from HEAD) =====
  const mapProductFormToApiPayload = (form: Partial<Product>) => ({
    title: form.name,
    description: form.description || "",
    notes: form.notes || "",
    start_date: form.launchDate
      ? new Date(form.launchDate).toISOString()
      : new Date().toISOString(),
    end_date: form.endDate ? new Date(form.endDate).toISOString() : null,
    created_by: userId,
    updated_by: userId,
    status: form.status || "Active",
    tenant_id: tenantId,
    tags: form.tags || [],
  });

  const handleCreateProduct = async () => {
    if (!productFormData.name) {
      toast.warning("Please enter product name");
      return;
    }
    if (!isDateNotInPast(productFormData.launchDate || "")) {
      toast.warning(
        "Launch date cannot be in the past. Please pick today or a future date.",
      );
      return;
    }
    if (productFormData.endDate && !isDateNotInPast(productFormData.endDate)) {
      toast.warning(
        "End date cannot be in the past. Please pick today or a future date.",
      );
      return;
    }
    setSaving(true);
    try {
      const payload = mapProductFormToApiPayload(productFormData);
      const response = await fetch(`${API_ROOT_URL}/products`, {
        method: "POST",
        headers: authenticatedJsonHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || "Failed to create product");
      }
      const result = await response.json();
      const created = result.data ?? result;

      const newProductId = created.product_id ?? created.id;
      const cleanAssetIds = (productFormData.assetIds || []).filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      );
      if (newProductId && cleanAssetIds.length > 0) {
        const linkRes = await fetch(
          `${API_ROOT_URL}/product-assets/${newProductId}`,
          {
            method: "PUT",
            headers: authenticatedJsonHeaders(),
            body: JSON.stringify({
              asset_ids: cleanAssetIds,
              updated_by: userId,
            }),
          },
        );
        if (!linkRes.ok) {
          toast.warning("Product created, but linking assets failed.");
        }
      }

      const newProduct: Product = {
        id: created.product_id ?? created.id,
        name: created.title,
        description: created.description,
        launchDate: created.start_date ? created.start_date.split("T")[0] : "",
        endDate: created.end_date ? created.end_date.split("T")[0] : "",
        notes: created.notes,
        tags: Array.isArray(created.tags)
          ? created.tags
          : typeof created.tags === "string"
            ? created.tags
                .replace(/[{}"]/g, "")
                .split("•")
                .map((t: string) => t.trim())
                .filter(Boolean)
            : [],
        status: created.status,
        image: created.image_url || productFormData.image || "",
        video: created.video_url || productFormData.video || "",
      };
      setProducts([newProduct, ...products]); // instant feedback
      setProductTotal((t) => t + 1); // count updates immediately
      if (productPage === 1) {
        loadProducts(); // already on page 1, so refetch directly
      } else {
        setProductPage(1); // the product effect refetches
      }
      setShowProductModal(false);
      resetProductForm();
      toast.success("Product created successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error creating product. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const productFieldMap: Record<string, string> = {
    name: "title",
    description: "description",
    notes: "notes",
    launchDate: "start_date",
    endDate: "end_date",
    status: "status",
    tags: "tags",
  };

  const buildProductDiffPayload = (
    original: Product,
    updated: Partial<Product>,
  ): Record<string, any> => {
    const diff: Record<string, any> = {};
    Object.entries(productFieldMap).forEach(([uiField, apiField]) => {
      const originalValue = (original as any)[uiField];
      const updatedValue = (updated as any)[uiField];
      if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
        if (uiField === "launchDate" || uiField === "endDate") {
          diff[apiField] = updatedValue
            ? new Date(updatedValue).toISOString()
            : null;
        } else {
          diff[apiField] = updatedValue;
        }
      }
    });
    return diff;
  };

  const handleUpdateProduct = async () => {
    if (!selectedProduct) return;
    if (!isDateNotInPast(productFormData.launchDate || "")) {
      toast.warning(
        "Launch date cannot be in the past. Please pick today or a future date.",
      );
      return;
    }
    if (productFormData.endDate && !isDateNotInPast(productFormData.endDate)) {
      toast.warning(
        "End date cannot be in the past. Please pick today or a future date.",
      );
      return;
    }
    setSaving(true);
    try {
      const cleanAssetIds = (productFormData.assetIds || []).filter(
        (id): id is string => typeof id === "string" && id.length > 0,
      );
      const changedFields = buildProductDiffPayload(
        selectedProduct,
        productFormData,
      );
      const assetsChanged =
        JSON.stringify([...(selectedProduct.assetIds || [])].sort()) !==
        JSON.stringify([...cleanAssetIds].sort());

      if (Object.keys(changedFields).length === 0 && !assetsChanged) {
        toast.info("No changes to save");
        setSaving(false);
        return;
      }

      let updated: any = selectedProduct;

      if (Object.keys(changedFields).length > 0) {
        const payload = {
          ...changedFields,
          updated_by: userId,
          tenant_id: tenantId,
        };
        const response = await fetch(
          `${API_ROOT_URL}/products/${selectedProduct.id}`,
          {
            method: "PUT",
            headers: authenticatedJsonHeaders(),
            body: JSON.stringify(payload),
          },
        );
        if (!response.ok) {
          const errText = await response.text();
          throw new Error(errText || "Failed to update product");
        }
        updated = await response.json();
      }

      // Sync linked assets independently of other field changes
      if (assetsChanged) {
        const linkRes = await fetch(
          `${API_ROOT_URL}/product-assets/${selectedProduct.id}`,
          {
            method: "PUT",
            headers: authenticatedJsonHeaders(),
            body: JSON.stringify({
              asset_ids: cleanAssetIds,
              updated_by: userId,
            }),
          },
        );
        const linkResult = await linkRes.json().catch(() => ({}));
        if (!linkRes.ok || linkResult.success === false) {
          throw new Error(
            linkResult.message || "Failed to link assets to product",
          );
        }
      }

      setProducts(
        products.map((p) =>
          p.id === selectedProduct.id
            ? {
                ...p,
                name: updated.title ?? p.name,
                description: updated.description ?? p.description,
                notes: updated.notes ?? p.notes,
                launchDate: updated.start_date ?? p.launchDate,
                endDate: updated.end_date ?? p.endDate,
                status: updated.status ?? p.status,
                tags: updated.tags ?? p.tags,
                assetIds: cleanAssetIds,
              }
            : p,
        ),
      );
      setShowProductModal(false);
      setSelectedProduct(null);
      resetProductForm();
      toast.success("Product updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error updating product. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (product: Product) => {
    const confirmed = await confirmToast(
      `Are you sure you want to delete "${product.name}"?`,
    );
    if (!confirmed) return;

    try {
      const res = await fetch(
        `${API_ROOT_URL}/products/${product.id}?tenant_id=${tenantId}`,
        {
          method: "DELETE",
          headers: authenticatedJsonHeaders(),
        },
      );
      const result = await res.json();

      if (res.status === 409) {
        toast.warning(
          result.message ||
            "This product is linked to one or more assets and cannot be deleted.",
        );
        return;
      }

      if (!res.ok || !result.success) {
        throw new Error(result.message || "Failed to delete product");
      }

      toast.success("Product deleted successfully!");
      setShowProductModal(false);
      setShowProductDetailsModal(false);
      setSelectedProduct(null);
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
      setProductTotal((t) => Math.max(0, t - 1));
      if (products.length === 1 && productPage > 1) {
        setProductPage(productPage - 1); // the page effect refetches
      } else {
        await loadProducts();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to delete product");
    }
  };

  // ===== CONTENT CRUD (Mock/local) =====
  const handleCreateContent = async () => {
    if (!contentFormData.name) {
      toast.warning("Please enter content name");
      return;
    }
    const newContent: Content = {
      id: Date.now().toString(),
      name: contentFormData.name || "",
      dateOfCreation: contentFormData.dateOfCreation || getLocalToday(),
      notes: contentFormData.notes || "",
      tags: contentFormData.tags || [],
      status: (contentFormData.status as "Active") || "Archived",
      image: contentFormData.image || "",
      video: contentFormData.video || "",
    };
    setContents([newContent, ...contents]);
    setShowContentModal(false);
    resetContentForm();
    toast.success("Content created successfully!");
  };

  const handleUpdateContent = async () => {
    if (!selectedContent) return;
    const updatedContents = contents.map((content) =>
      content.id === selectedContent.id
        ? { ...content, ...contentFormData }
        : content,
    );
    setContents(updatedContents);
    setShowContentModal(false);
    setSelectedContent(null);
    resetContentForm();
    toast.success("Content updated successfully!");
  };

  const handleDeleteContent = async (content: Content) => {
    const confirmed = await confirmToast(
      `Are you sure you want to delete "${content.name}"?`,
    );
    if (!confirmed) return;

    setContents(contents.filter((c) => c.id !== content.id));
    toast.success("Content deleted successfully!");
  };

  // ===== EVENT CRUD (API from HEAD) =====
  const mapEventFormToApiPayload = (form: Partial<Event>) => ({
    title: form.eventName,
    description: form.description || "",
    notes: form.notes || "",
    start_date: form.eventDate
      ? new Date(form.eventDate).toISOString()
      : new Date().toISOString(),
    end_date: form.endDate ? new Date(form.endDate).toISOString() : null,
    created_by: userId,
    updated_by: userId,
    status: form.status || "Scheduled",
    tenant_id: tenantId,
    tags: form.tags || [],
  });

  const handleCreateEvent = async () => {
    if (!eventFormData.eventName) {
      toast.warning("Please enter event name");
      return;
    }
    if (!isDateNotInPast(eventFormData.eventDate || "")) {
      toast.warning(
        "Event date cannot be in the past. Please pick today or a future date.",
      );
      return;
    }
    if (eventFormData.endDate && !isDateNotInPast(eventFormData.endDate)) {
      toast.warning(
        "End date cannot be in the past. Please pick today or a future date.",
      );
      return;
    }
    setSaving(true);
    try {
      const payload = mapEventFormToApiPayload(eventFormData);
      const response = await fetch(`${API_ORIGIN}/events`, {
        method: "POST",
        headers: authenticatedJsonHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || "Failed to create event");
      }
      const result = await response.json();
      const created = result.data ?? result;

      const newEvent: Event = {
        id: created.event_id ?? created.id,
        eventName: created.title,
        description: created.description,
        eventDate: created.start_date ? created.start_date.split("T")[0] : "",
        endDate: created.end_date ? created.end_date.split("T")[0] : "",
        notes: created.notes,
        tags: Array.isArray(created.tags)
          ? created.tags
          : typeof created.tags === "string"
            ? created.tags
                .replace(/[{}"]/g, "")
                .split("•")
                .map((t: string) => t.trim())
                .filter(Boolean)
            : [],
        status: created.status,
        image: eventFormData.image || "",
        video: eventFormData.video || "",
      };
      setEvents([newEvent, ...events]); // instant feedback
      setEventsTotal((t) => t + 1); // tab count updates immediately
      setEventsPage(1);
      loadEvents(1, eventsPageSize); // sync with the server
      setShowEventModal(false);
      resetEventForm();
      toast.success("Event created successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error creating event. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const eventFieldMap: Record<string, string> = {
    eventName: "title",
    description: "description",
    notes: "notes",
    eventDate: "start_date",
    endDate: "end_date",
    status: "status",
    tags: "tags",
  };

  const buildEventDiffPayload = (
    original: Event,
    updated: Partial<Event>,
  ): Record<string, any> => {
    const diff: Record<string, any> = {};
    Object.entries(eventFieldMap).forEach(([uiField, apiField]) => {
      const originalValue = (original as any)[uiField];
      const updatedValue = (updated as any)[uiField];
      if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
        if (uiField === "eventDate" || uiField === "endDate") {
          diff[apiField] = updatedValue
            ? new Date(updatedValue).toISOString()
            : null;
        } else {
          diff[apiField] = updatedValue;
        }
      }
    });
    return diff;
  };

  const handleUpdateEvent = async () => {
    if (!selectedEvent) return;
    if (!isDateNotInPast(eventFormData.eventDate || "")) {
      toast.warning(
        "Event date cannot be in the past. Please pick today or a future date.",
      );
      return;
    }
    if (eventFormData.endDate && !isDateNotInPast(eventFormData.endDate)) {
      toast.warning(
        "End date cannot be in the past. Please pick today or a future date.",
      );
      return;
    }
    setSaving(true);
    try {
      const changedFields = buildEventDiffPayload(selectedEvent, eventFormData);
      if (Object.keys(changedFields).length === 0) {
        toast.info("No changes to save");
        setSaving(false);
        return;
      }
      const payload = {
        ...changedFields,
        updated_by: userId,
        tenant_id: tenantId,
      };
      const response = await fetch(`${API_ORIGIN}/events/${selectedEvent.id}`, {
        method: "PUT",
        headers: authenticatedJsonHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(errText || "Failed to update event");
      }
      const result = await response.json();
      const updated = result.data ?? result;
      setEvents(
        events.map((ev) =>
          ev.id === selectedEvent.id
            ? {
                ...ev,
                eventName: updated.title ?? ev.eventName,
                description: updated.description ?? ev.description,
                notes: updated.notes ?? ev.notes,
                eventDate: updated.start_date
                  ? updated.start_date.split("T")[0]
                  : ev.eventDate,
                endDate: updated.end_date
                  ? updated.end_date.split("T")[0]
                  : ev.endDate,
                status: updated.status ?? ev.status,
                tags: updated.tags ?? ev.tags,
              }
            : ev,
        ),
      );
      setShowEventModal(false);
      setSelectedEvent(null);
      resetEventForm();
      toast.success("Event updated successfully!");
    } catch (error) {
      console.error(error);
      toast.error("Error updating event. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEvent = async (event: Event) => {
    const confirmed = await confirmToast(
      `Are you sure you want to delete "${event.eventName}"?`,
    );
    if (!confirmed) return;

    try {
      const res = await fetch(
        `${API_ORIGIN}/events/${event.id}?tenant_id=${tenantId}`,
        {
          method: "DELETE",
          headers: authenticatedJsonHeaders(),
        },
      );
      const result = await res.json();

      if (res.status === 409) {
        toast.warning(
          result.message ||
            "This event is linked to a campaign and cannot be deleted.",
        );
        return;
      }

      if (!res.ok || !result.success) {
        throw new Error(result.message || "Failed to delete event");
      }

      toast.success("Event deleted successfully!");
      setShowEventModal(false);
      setShowEventDetailsModal(false);
      setSelectedEvent(null);
      setEvents((prev) => prev.filter((e) => e.id !== event.id));
      setEventsTotal((t) => Math.max(0, t - 1));
      if (events.length === 1 && eventsPage > 1) {
        setEventsPage(eventsPage - 1); // the page effect refetches
      } else {
        await loadEvents(eventsPage, eventsPageSize);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to delete event");
    }
  };

  // ===== RESET FORMS =====
  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      startDate: getLocalToday(),
      endDate: "",
      notes: "",
      tags: [],
      status: "Active",
      image: "",
      video: "",
      eventIds: [], // NEW
      assetIds: [], // NEW
    });
    setImagePreview("");
    setVideoPreview("");
    setNewTag("");
    setActiveMediaTab("image");
  };

  const resetProductForm = () => {
    setProductFormData({
      name: "",
      description: "",
      launchDate: "",
      endDate: "",
      notes: "",
      tags: [],
      status: "Active",
      image: "",
      video: "",
      assetIds: [], // NEW
    });
  };

  const resetContentForm = () => {
    setContentFormData({
      name: "",
      dateOfCreation: getLocalToday(),
      notes: "",
      tags: [],
      status: "Active",
      image: "",
      video: "",
    });
  };

  const resetEventForm = () => {
    setEventFormData({
      eventName: "",
      description: "",
      eventDate: "",
      endDate: "",
      notes: "",
      tags: [],
      status: "Scheduled",
      image: "",
      video: "",
    });
  };

  const findAssetById = (id: string) =>
    campaignAssets.find((a) => a.id === id) ?? assets.find((a) => a.id === id);

  // ===== TAG HANDLING =====
  const addTag = () => {
    if (newTag && !formData.tags?.includes(newTag)) {
      setFormData({ ...formData, tags: [...(formData.tags || []), newTag] });
      setNewTag("");
    }
  };
  const removeTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter((t) => t !== tag) || [],
    });
  };

  const toggleEventSelection = (eventId: string) => {
    setFormData((prev) => {
      const current = prev.eventIds || [];
      const updated = current.includes(eventId)
        ? current.filter((id) => id !== eventId)
        : [...current, eventId];
      return { ...prev, eventIds: updated };
    });
  };

  const removeSelectedEvent = (eventId: string) => {
    setFormData((prev) => ({
      ...prev,
      eventIds: (prev.eventIds || []).filter((id) => id !== eventId),
    }));
  };

  const toggleAssetSelection = (assetId: string) => {
    setFormData((prev) => {
      const current = prev.assetIds || [];
      const updated = current.includes(assetId)
        ? current.filter((id) => id !== assetId)
        : [...current, assetId];
      return { ...prev, assetIds: updated };
    });
  };

  const removeSelectedAsset = (assetId: string) => {
    setFormData((prev) => ({
      ...prev,
      assetIds: (prev.assetIds || []).filter((id) => id !== assetId),
    }));
  };

  const toggleProductAssetSelection = (assetId: string) => {
    setProductFormData((prev) => {
      const current = prev.assetIds || [];
      const updated = current.includes(assetId)
        ? current.filter((id) => id !== assetId)
        : [...current, assetId];
      return { ...prev, assetIds: updated };
    });
  };

  const removeSelectedProductAsset = (assetId: string) => {
    setProductFormData((prev) => ({
      ...prev,
      assetIds: (prev.assetIds || []).filter((id) => id !== assetId),
    }));
  };

  const addProductTag = () => {
    if (newTag && !productFormData.tags?.includes(newTag)) {
      setProductFormData({
        ...productFormData,
        tags: [...(productFormData.tags || []), newTag],
      });
      setNewTag("");
    }
  };
  const removeProductTag = (tag: string) => {
    setProductFormData({
      ...productFormData,
      tags: productFormData.tags?.filter((t) => t !== tag) || [],
    });
  };

  const addContentTag = () => {
    if (newTag && !contentFormData.tags?.includes(newTag)) {
      setContentFormData({
        ...contentFormData,
        tags: [...(contentFormData.tags || []), newTag],
      });
      setNewTag("");
    }
  };
  const removeContentTag = (tag: string) => {
    setContentFormData({
      ...contentFormData,
      tags: contentFormData.tags?.filter((t) => t !== tag) || [],
    });
  };

  const addEventTag = () => {
    if (newTag && !eventFormData.tags?.includes(newTag)) {
      setEventFormData({
        ...eventFormData,
        tags: [...(eventFormData.tags || []), newTag],
      });
      setNewTag("");
    }
  };
  const removeEventTag = (tag: string) => {
    setEventFormData({
      ...eventFormData,
      tags: eventFormData.tags?.filter((t) => t !== tag) || [],
    });
  };

  // ===== UI HELPERS =====
  const getStatusColor = (status: string) => {
    return status === "Active"
      ? "bg-green-100 text-green-700"
      : status === "Scheduled"
        ? "bg-yellow-100 text-yellow-700"
        : "bg-gray-100 text-gray-700";
  };
  const getStatusIcon = (status: string) => {
    return status === "Active" ? "✅" : "📦";
  };

  // ===== FILTERS =====
  const getFilteredCampaigns = () => {
    // Status filter, search, and pagination now happen server-side
    // (see loadCampaigns) — `campaigns` is already the current page's data.
    return campaigns;
  };

  const getFilteredContents = () => {
    let filtered = [...contents];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.name.toLowerCase().includes(term) ||
          c.notes.toLowerCase().includes(term) ||
          c.tags.some((tag) => tag.toLowerCase().includes(term)),
      );
    }
    return filtered;
  };

  const getFilteredEvents = () => {
    // Status filter, search, and pagination now happen server-side
    // (see loadEvents) — `events` is already the current page's data.
    return events;
  };

  // ===== ARCHIVE COUNTS =====
  const archiveCounts: ArchiveCounts = {
    campaigns: campaigns.filter((c) => c.status === "Archived").length,
    products: products.filter((p) => p.status === "Archived").length,
    contents: contents.filter((c) => c.status === "Archived").length,
    events: events.filter((e) => e.status === "Archived").length,
    total:
      campaigns.filter((c) => c.status === "Archived").length +
      products.filter((p) => p.status === "Archived").length +
      contents.filter((c) => c.status === "Archived").length +
      events.filter((e) => e.status === "Archived").length,
  };

  const filteredCampaigns = getFilteredCampaigns();
  const filteredProducts = products;
  const filteredContents = getFilteredContents();
  const filteredEvents = getFilteredEvents();

  // ===== MEDIA HANDLING =====
  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.warning("Image size should be less than 5MB");
        return;
      }
      if (!file.type.match("image.*")) {
        toast.warning("Please upload an image file");
        return;
      }
      const base64 = await convertToBase64(file);
      setImagePreview(base64);
      setFormData((prev) => ({ ...prev, image: base64, video: "" }));
      setActiveMediaTab("image");
    }
  };

  const handleVideoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 20 * 1024 * 1024) {
        toast.warning("Video size should be less than 20MB");
        return;
      }
      if (!file.type.match("video.*")) {
        toast.warning("Please upload a video file");
        return;
      }
      const base64 = await convertToBase64(file);
      setVideoPreview(base64);
      setFormData((prev) => ({ ...prev, video: base64, image: "" }));
      setActiveMediaTab("video");
    }
  };

  // ===== RENDER CARDS =====
  const renderCampaignCard = (campaign: Campaign) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group">
      <div className="relative h-48 overflow-hidden bg-gray-100">
        {campaign.image ? (
          <img
            src={campaign.image}
            alt={campaign.title}
            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gradient-to-br from-gray-100 to-gray-200">
            <span className="text-4xl">🎯</span>
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span
            className={`px-2 py-1 text-xs rounded-full ${getStatusColor(campaign.status)} flex items-center gap-1`}
          >
            {getStatusIcon(campaign.status)} {campaign.status}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-800 truncate text-lg">
          {campaign.title}
        </h3>
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
          {campaign.notes}
        </p>
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <span>📅</span>
            <span>
              {formatDate(campaign.startDate)} - {formatDate(campaign.endDate)}
            </span>
          </div>
        </div>
        {campaign.tags && campaign.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {campaign.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="text-xs text-gray-400">
                #{tag}
              </span>
            ))}
          </div>
        )}
        <div className="mt-4 flex gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={async () => {
              setSelectedCampaign(campaign);
              try {
                const res = await fetch(
                  `${API_ROOT_URL}/campaign-assets/${campaign.id}`,
                  {
                    headers: authenticatedJsonHeaders(),
                  },
                );

                const result = await res.json();

                let linkedAssetIds: string[] = [];

                if (result.success) {
                  linkedAssetIds = result.data
                    .map((a: any) => a.asset_id ?? a.id)
                    .filter(
                      (id: unknown) => typeof id === "string" && id.length > 0,
                    );
                  // keep linked assets resolvable for chips/picker
                  setCampaignAssets((prev) => {
                    const known = new Set(prev.map((a) => a.id));
                    const extra = result.data
                      .map((a: any) => ({
                        ...a,
                        id: String(a.asset_id ?? a.id),
                      }))
                      .filter((a: Asset) => !known.has(a.id));
                    return [...extra, ...prev];
                  });
                }

                setFormData({
                  ...campaign,
                  startDate: campaign.startDate
                    ? campaign.startDate.split("T")[0]
                    : "",
                  endDate: campaign.endDate
                    ? campaign.endDate.split("T")[0]
                    : "",
                  assetIds: linkedAssetIds,
                });
              } catch (err) {
                console.error("Failed to load linked campaign assets", err);

                // Fallback
                setFormData(campaign);
              }
              setImagePreview(campaign.image || "");
              setShowEditModal(true);
              setActiveMediaTab(campaign.video ? "video" : "image");
            }}
            className="flex-1 text-center text-sm bg-blue-50 text-blue-600 px-2 py-1.5 rounded hover:bg-blue-100 transition"
          >
            Edit
          </button>
          <button
            onClick={() => {
              setSelectedCampaign(campaign);
              setShowDetailsModal(true);
            }}
            className="flex-1 text-center text-sm bg-gray-50 text-gray-600 px-2 py-1.5 rounded hover:bg-gray-100 transition"
          >
            Details
          </button>
          <button
            onClick={() => handleDeleteCampaign(campaign)}
            className="flex-1 text-center text-sm bg-red-50 text-red-600 px-2 py-1.5 rounded hover:bg-red-100 transition"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  const renderProductCard = (product: Product) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group">
      <div className="relative h-48 overflow-hidden bg-gray-100">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gradient-to-br from-gray-100 to-gray-200">
            <span className="text-4xl">📦</span>
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span
            className={`px-2 py-1 text-xs rounded-full ${getStatusColor(product.status)} flex items-center gap-1`}
          >
            {getStatusIcon(product.status)} {product.status}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-800 truncate text-lg">
          {product.name}
        </h3>
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
          {product.notes}
        </p>
        <div className="mt-3">
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <span>🚀 Launch: {formatDate(product.launchDate)}</span>
          </div>
        </div>
        {product.tags && product.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {product.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="text-xs text-gray-400">
                #{tag}
              </span>
            ))}
          </div>
        )}
        <div className="mt-4 flex gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={async () => {
              setSelectedProduct(product);
              setProductFormData({
                ...product,
                launchDate: product.launchDate
                  ? product.launchDate.split("T")[0]
                  : "",
                endDate: product.endDate ? product.endDate.split("T")[0] : "",
              });
              setShowProductModal(true);
              try {
                const res = await fetch(
                  `${API_ROOT_URL}/product-assets/${product.id}`,
                );
                const result = await res.json();
                if (result.success) {
                  const linkedIds: string[] = result.data
                    .map((a: any) => a.asset_id ?? a.id)
                    .filter(
                      (id: unknown): id is string =>
                        typeof id === "string" && id.length > 0,
                    );

                  // keep linked assets resolvable for the chips (they may not be in the first 200 loaded)
                  setAssets((prev) => {
                    const known = new Set(prev.map((x) => x.id));
                    const extra = result.data
                      .map((a: any) => ({
                        ...a,
                        id: String(a.asset_id ?? a.id),
                      }))
                      .filter((a: Asset) => !known.has(a.id));
                    return [...extra, ...prev];
                  });

                  setProductFormData((prev) => ({
                    ...prev,
                    assetIds: linkedIds,
                  }));
                  setSelectedProduct((prev) =>
                    prev ? { ...prev, assetIds: linkedIds } : prev,
                  );
                }
              } catch (err) {
                console.error("Failed to load linked assets for product", err);
              }
            }}
            className="flex-1 text-center text-sm bg-blue-50 text-blue-600 px-2 py-1.5 rounded hover:bg-blue-100 transition"
          >
            Edit
          </button>
          <button
            onClick={() => {
              setSelectedProduct(product);
              setShowProductDetailsModal(true);
            }}
            className="flex-1 text-center text-sm bg-gray-50 text-gray-600 px-2 py-1.5 rounded hover:bg-gray-100 transition"
          >
            Details
          </button>
          <button
            onClick={() => handleDeleteProduct(product)}
            className="flex-1 text-center text-sm bg-red-50 text-red-600 px-2 py-1.5 rounded hover:bg-red-100 transition"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  const renderContentCard = (content: Content) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group">
      <div className="relative h-48 overflow-hidden bg-gray-100">
        {content.image ? (
          <img
            src={content.image}
            alt={content.name}
            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gradient-to-br from-gray-100 to-gray-200">
            <span className="text-4xl">📄</span>
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span
            className={`px-2 py-1 text-xs rounded-full ${getStatusColor(content.status)} flex items-center gap-1`}
          >
            {getStatusIcon(content.status)} {content.status}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-800 truncate text-lg">
          {content.name}
        </h3>
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">
          {content.notes}
        </p>
        <div className="mt-3">
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <span>📅 Created: {formatDate(content.dateOfCreation)}</span>
          </div>
        </div>
        {content.tags && content.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {content.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="text-xs text-gray-400">
                #{tag}
              </span>
            ))}
          </div>
        )}
        <div className="mt-4 flex gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={() => {
              setSelectedContent(content);
              setContentFormData(content);
              setShowContentModal(true);
            }}
            className="flex-1 text-center text-sm bg-blue-50 text-blue-600 px-2 py-1.5 rounded hover:bg-blue-100 transition"
          >
            Edit
          </button>
          <button
            onClick={() => {
              setSelectedContent(content);
              setShowContentDetailsModal(true);
            }}
            className="flex-1 text-center text-sm bg-gray-50 text-gray-600 px-2 py-1.5 rounded hover:bg-gray-100 transition"
          >
            Details
          </button>
          <button
            onClick={() => handleDeleteContent(content)}
            className="flex-1 text-center text-sm bg-red-50 text-red-600 px-2 py-1.5 rounded hover:bg-red-100 transition"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  const renderEventCard = (event: Event) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group">
      <div className="relative h-48 overflow-hidden bg-gray-100">
        {event.image ? (
          <img
            src={event.image}
            alt={event.eventName}
            className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gradient-to-br from-gray-100 to-gray-200">
            <span className="text-4xl">🎪</span>
          </div>
        )}
        <div className="absolute top-2 right-2">
          <span
            className={`px-2 py-1 text-xs rounded-full ${getStatusColor(event.status)} flex items-center gap-1`}
          >
            {getStatusIcon(event.status)} {event.status}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-800 truncate text-lg">
          {event.eventName}
        </h3>
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{event.notes}</p>
        <div className="mt-3">
          <div className="flex items-center gap-1 text-xs text-gray-400">
            <span>📅 Event: {formatDate(event.eventDate)}</span>
          </div>
        </div>
        {event.tags && event.tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {event.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="text-xs text-gray-400">
                #{tag}
              </span>
            ))}
          </div>
        )}
        <div className="mt-4 flex gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={() => {
              setSelectedEvent(event);
              setEventFormData({
                ...event,
                eventDate: event.eventDate ? event.eventDate.split("T")[0] : "",
                endDate: event.endDate ? event.endDate.split("T")[0] : "",
              });
              setShowEventModal(true);
            }}
            className="flex-1 text-center text-sm bg-blue-50 text-blue-600 px-2 py-1.5 rounded hover:bg-blue-100 transition"
          >
            Edit
          </button>
          <button
            onClick={() => {
              setSelectedEvent(event);
              setShowEventDetailsModal(true);
            }}
            className="flex-1 text-center text-sm bg-gray-50 text-gray-600 px-2 py-1.5 rounded hover:bg-gray-100 transition"
          >
            Details
          </button>
          <button
            onClick={() => handleDeleteEvent(event)}
            className="flex-1 text-center text-sm bg-red-50 text-red-600 px-2 py-1.5 rounded hover:bg-red-100 transition"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  // ===== JSX =====
  return (
    <div className="space-y-6">
      {!view || view === "summary" ? (
        <>
          {/* Header Banner */}
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <div className="text-4xl">📋</div>
              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-purple-900 text-lg">
                      Campaigns & Events Management
                    </h3>
                    <p className="text-sm text-purple-700 mt-1">
                      Create and manage Campaigns, Products, Content, and Events
                      with Public/Private Archive visibility.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4 mt-3">
                  <div className="flex items-center gap-1 text-xs text-purple-600">
                    <span>✓</span> Campaign Section
                  </div>
                  <div className="flex items-center gap-1 text-xs text-purple-600">
                    <span>✓</span> Product Section
                  </div>
                  <div className="flex items-center gap-1 text-xs text-purple-600">
                    <span>✓</span> Content Section
                  </div>
                  <div className="flex items-center gap-1 text-xs text-purple-600">
                    <span>✓</span> Events Section
                  </div>
                  <div className="flex items-center gap-1 text-xs text-purple-600">
                    <span>✓</span> Public/Private Archive
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl mb-2">🎯</div>
              <div className="text-2xl font-bold text-gray-800">
                {campaigns.length}
              </div>
              <div className="text-sm text-gray-500">Campaigns</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl mb-2">📦</div>
              <div className="text-2xl font-bold text-gray-800">
                {products.length}
              </div>
              <div className="text-sm text-gray-500">Products</div>
            </div>
            {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl mb-2">📄</div>
              <div className="text-2xl font-bold text-gray-800">
                {contents.length}
              </div>
              <div className="text-sm text-gray-500">Content</div>
            </div> */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl mb-2">🎪</div>
              <div className="text-2xl font-bold text-gray-800">
                {events.length}
              </div>
              <div className="text-sm text-gray-500">Events</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl mb-2">✅</div>
              <div className="text-2xl font-bold text-green-600">
                {campaigns.filter((c) => c.status === "Active").length +
                  products.filter((p) => p.status === "Active").length +
                  contents.filter((c) => c.status === "Active").length +
                  events.filter((e) => e.status === "Active").length}
              </div>
              <div className="text-sm text-gray-500">Active Items</div>
            </div>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="text-2xl mb-2">📦</div>
              <div className="text-2xl font-bold text-gray-700">
                {archiveCounts.total}
              </div>
              <div className="text-sm text-gray-500 mb-2">Archived Items</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="text-2xl mb-2">🌐</div>
              <div className="text-2xl font-bold text-blue-600">
                {archiveVisibility === "Public" ? "Public" : "Private"}
              </div>
              <div className="text-sm text-gray-500">Current View</div>
            </div>
          </div>
        </>
      ) : (
        <div>
          <h2 className="text-2xl font-bold text-gray-800">
            {singleViewHeading[view]?.title}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {singleViewHeading[view]?.subtitle}
          </p>
        </div>
      )}

      {/* Tab Navigation */}
      {!view && (
        <div className="border-b border-gray-200">
          <nav className="flex gap-6">
            <button
              onClick={() => setActiveTab("campaigns")}
              className={`pb-3 px-1 font-medium transition ${
                activeTab === "campaigns"
                  ? "border-b-2 border-purple-600 text-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              🎯 Campaigns ({campaignsTotal})
            </button>
            <button
              onClick={() => setActiveTab("products")}
              className={`pb-3 px-1 font-medium transition ${
                activeTab === "products"
                  ? "border-b-2 border-purple-600 text-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              📦 Products ({productTotal})
            </button>
            <button
              onClick={() => setActiveTab("contents")}
              className={`pb-3 px-1 font-medium transition ${
                activeTab === "contents"
                  ? "border-b-2 border-purple-600 text-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              📄 Content ({filteredContents.length})
            </button>
            <button
              onClick={() => setActiveTab("events")}
              className={`pb-3 px-1 font-medium transition ${
                activeTab === "events"
                  ? "border-b-2 border-purple-600 text-purple-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              🎪 Events ({eventsTotal})
            </button>
          </nav>
        </div>
      )}

      {/* Filters and Actions Bar */}
      {view !== "summary" && (
        <>
          <div className="flex justify-between items-center flex-wrap gap-4">
            <div className="flex gap-3 flex-wrap">
              {activeTab === "products" ? (
                <>
                  <div className="relative">
                    <select
                      value={productStatusFilter}
                      onChange={(e) => setProductStatusFilter(e.target.value)}
                      className="appearance-none min-w-[170px] h-11 px-4 pr-10 bg-white border border-gray-300 rounded-xl text-sm font-medium text-gray-700 shadow-sm cursor-pointer transition-all duration-200 hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="all">All Status</option>
                      <option value="Active">Active</option>
                      <option value="Archived">Archived</option>
                    </select>
                    <svg
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      value={productSearchInput}
                      onChange={(e) => setProductSearchInput(e.target.value)}
                      placeholder="Search products..."
                      className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 w-64"
                    />
                    <span className="absolute left-3 top-2.5 text-gray-400">
                      🔍
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="relative">
                    <select
                      value={filterStatus}
                      onChange={(e) => {
                        setFilterStatus(e.target.value);
                        setCampaignsPage(1);
                        setEventsPage(1);
                      }}
                      className="appearance-none min-w-[170px] h-11 px-4 pr-10 bg-white border border-gray-300 rounded-xl text-sm font-medium text-gray-700 shadow-sm cursor-pointer transition-all duration-200 hover:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    >
                      <option value="all">All Status</option>
                      <option value="Active">Active</option>
                      <option value="Archived">Archived</option>
                    </select>
                    <svg
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search..."
                      className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 w-64"
                    />
                    <span className="absolute left-3 top-2.5 text-gray-400">
                      🔍
                    </span>
                    {searchTerm && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm("")}
                        className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600"
                        aria-label="Clear search"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            {activeTab === "campaigns" && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 shadow-sm"
              >
                <span className="text-xl">+</span> Create Campaign
              </button>
            )}
            {activeTab === "products" && (
              <button
                onClick={() => setShowProductModal(true)}
                className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 shadow-sm"
              >
                <span className="text-xl">+</span> Create Product
              </button>
            )}
            {activeTab === "contents" && (
              <button
                onClick={() => setShowContentModal(true)}
                className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 shadow-sm"
              >
                <span className="text-xl">+</span> Create Content
              </button>
            )}
            {activeTab === "events" && (
              <button
                onClick={() => setShowEventModal(true)}
                className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 shadow-sm"
              >
                <span className="text-xl">+</span> Create Event
              </button>
            )}
          </div>

          {/* Content Grids */}
          {activeTab === "campaigns" &&
            (campaignsLoading ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <div className="text-6xl mb-3 animate-pulse">🎯</div>
                <p className="text-gray-500">Loading campaigns...</p>
              </div>
            ) : filteredCampaigns.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 content-start">
                  {filteredCampaigns.map((campaign) =>
                    renderCampaignCard(campaign),
                  )}
                </div>
                <PaginationControls
                  page={campaignsPage}
                  totalPages={campaignsTotalPages}
                  total={campaignsTotal}
                  pageSize={campaignsPageSize}
                  loading={campaignsLoading}
                  onPageChange={setCampaignsPage}
                  onPageSizeChange={(size) => {
                    setCampaignsPageSize(size);
                    setCampaignsPage(1);
                  }}
                />
              </>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <div className="text-6xl mb-3">🎯</div>
                <p className="text-gray-500">No campaigns found</p>
                <p className="text-sm text-gray-400 mt-1">
                  Click "Create Campaign" to get started
                </p>
              </div>
            ))}

          {activeTab === "products" &&
            (filteredProducts.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {filteredProducts.map((product) =>
                    renderProductCard(product),
                  )}
                </div>
                <div className="flex items-center justify-end flex-wrap gap-3 mt-4">
                  <span className="text-sm text-gray-500">
                    Showing {(productPage - 1) * productLimit + 1}-
                    {Math.min(productPage * productLimit, productTotal)} of{" "}
                    {productTotal}
                  </span>
                  <button
                    disabled={productPage <= 1}
                    onClick={() => setProductPage((p) => p - 1)}
                    className="px-3 py-1.5 border rounded-lg disabled:opacity-40 text-sm"
                  >
                    Prev
                  </button>
                  <button
                    disabled={productPage >= productTotalPages}
                    onClick={() => setProductPage((p) => p + 1)}
                    className="px-3 py-1.5 border rounded-lg disabled:opacity-40 text-sm"
                  >
                    Next
                  </button>
                  <select
                    value={productLimit}
                    onChange={(e) => setProductLimit(Number(e.target.value))}
                    className="px-2 py-1.5 border rounded-lg text-sm"
                  >
                    {[12, 24, 48, 96].map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <div className="text-6xl mb-3">📦</div>
                <p className="text-gray-500">No products found</p>
                <p className="text-sm text-gray-400 mt-1">
                  Click "Create Product" to get started
                </p>
              </div>
            ))}

          {activeTab === "contents" &&
            (filteredContents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredContents.map((content) => renderContentCard(content))}
              </div>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <div className="text-6xl mb-3">📄</div>
                <p className="text-gray-500">No content found</p>
                <p className="text-sm text-gray-400 mt-1">
                  Click "Create Content" to get started
                </p>
              </div>
            ))}

          {activeTab === "events" &&
            (eventsLoading ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <div className="text-6xl mb-3 animate-pulse">🎪</div>
                <p className="text-gray-500">Loading events...</p>
              </div>
            ) : filteredEvents.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 content-start">
                  {filteredEvents.map((event) => renderEventCard(event))}
                </div>
                <PaginationControls
                  page={eventsPage}
                  totalPages={eventsTotalPages}
                  total={eventsTotal}
                  pageSize={eventsPageSize}
                  loading={eventsLoading}
                  onPageChange={setEventsPage}
                  onPageSizeChange={(size) => {
                    setEventsPageSize(size);
                    setEventsPage(1);
                  }}
                />
              </>
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <div className="text-6xl mb-3">🎪</div>
                <p className="text-gray-500">No events found</p>
                <p className="text-sm text-gray-400 mt-1">
                  Click "Create Event" to get started
                </p>
              </div>
            ))}
        </>
      )}

      {/* ===== MODALS ===== */}
      {/* Create Campaign Modal */}
      {showCreateModal && !showAssetPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    Create Campaign
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Fill in the campaign details
                  </p>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-black hover:text-black text-4xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                  {/* Media Upload */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Media
                    </label>
                    <div className="flex gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => setActiveMediaTab("image")}
                        className={`px-4 py-2 rounded transition ${
                          activeMediaTab === "image"
                            ? "bg-purple-600 text-white"
                            : "bg-gray-200"
                        }`}
                      >
                        📷 Image
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveMediaTab("video")}
                        className={`px-4 py-2 rounded transition ${
                          activeMediaTab === "video"
                            ? "bg-purple-600 text-white"
                            : "bg-gray-200"
                        }`}
                      >
                        🎥 Video
                      </button>
                    </div>
                    {activeMediaTab === "image" && (
                      <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-purple-500"
                        onClick={() => imageInputRef.current?.click()}
                      >
                        {imagePreview ? (
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded"
                          />
                        ) : (
                          <p className="text-gray-500">Click to upload image</p>
                        )}
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                        />
                      </div>
                    )}
                    {activeMediaTab === "video" && (
                      <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-purple-500"
                        onClick={() => videoInputRef.current?.click()}
                      >
                        {videoPreview ? (
                          <video
                            src={videoPreview}
                            className="max-h-48 mx-auto rounded"
                            controls
                          />
                        ) : (
                          <p className="text-gray-500">Click to upload video</p>
                        )}
                        <input
                          ref={videoInputRef}
                          type="file"
                          accept="video/*"
                          className="hidden"
                          onChange={handleVideoUpload}
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      placeholder="Campaign title"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Notes
                    </label>
                    <textarea
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
                      rows={3}
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          notes: e.target.value,
                        }))
                      }
                      placeholder="Additional notes"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Description
                    </label>
                    <textarea
                      className="w-full p-2 border rounded focus:ring-2 focus:ring-purple-500"
                      rows={2}
                      value={formData.description}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder="Short description"
                    />
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Schedule</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Start Date
                        </label>
                        <input
                          type="date"
                          min={getLocalToday()}
                          className="w-full p-2 border rounded"
                          value={formData.startDate}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              startDate: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          End Date
                        </label>
                        <input
                          type="date"
                          min={formData.startDate || getLocalToday()}
                          className="w-full p-2 border rounded"
                          value={formData.endDate}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              endDate: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Status</h3>
                    <select
                      className="w-full p-2 border rounded"
                      value={formData.status}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          status: e.target.value as "Active" | "Archived",
                        }))
                      }
                    >
                      <option value="Active">Active</option>
                      <option value="Archived">Archived</option>
                    </select>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Tags</h3>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.tags?.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gray-200 text-gray-700 text-sm rounded-full"
                        >
                          #{tag}
                          <button
                            onClick={() => removeTag(tag)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && addTag()}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                        placeholder="Add tag..."
                      />
                      <button
                        onClick={addTag}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Events Section */}
                    <div className="bg-gray-50 p-4 rounded-lg min-w-0">
                      <div className="flex items-center justify-between mb-3">
                        <button
                          type="button"
                          onClick={() => setShowEventPicker(true)}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 text-sm"
                        >
                          <span className="text-lg">+</span> Add Events
                        </button>
                      </div>
                      <div className="border border-gray-300 rounded-lg p-3 min-h-[48px] max-h-32 overflow-y-auto flex flex-wrap gap-2 items-start bg-white w-full">
                        {(formData.eventIds || []).length === 0 && (
                          <span className="text-gray-400 text-sm">
                            No events linked
                          </span>
                        )}
                        {(formData.eventIds || []).map((id) => {
                          const event = events.find((e) => e.id === id);
                          if (!event) return null;
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center max-w-full gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                            >
                              <span className="truncate max-w-[220px]">
                                {event.eventName}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeSelectedEvent(id)}
                                className="text-purple-500 hover:text-purple-700 shrink-0"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {/* Assets Section */}
                    <div className="bg-gray-50 p-4 rounded-lg min-w-0">
                      <div className="flex items-center justify-between mb-3">
                        <button
                          type="button"
                          onClick={() => {
                            loadCampaignAssets();
                            setShowAssetPicker(true);
                          }}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 text-sm"
                        >
                          <span className="text-lg">+</span> Add Assets
                        </button>
                      </div>
                      <div className="border border-gray-300 rounded-lg p-3 min-h-[48px] max-h-32 overflow-y-auto flex flex-wrap gap-2 items-start bg-white w-full">
                        {(formData.assetIds || []).length === 0 && (
                          <span className="text-gray-400 text-sm">
                            No assets linked
                          </span>
                        )}
                        {(formData.assetIds || []).map((id) => {
                          const asset = findAssetById(id);
                          if (!asset) return null;
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center max-w-full gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                            >
                              <span className="truncate max-w-[220px]">
                                {asset.original_filename}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeSelectedAsset(id)}
                                className="text-purple-500 hover:text-purple-700 shrink-0"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCampaign}
                disabled={saving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {saving ? "Creating..." : "Create Campaign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Campaign Modal */}
      {showEditModal && selectedCampaign && !showAssetPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">
                    Edit Campaign
                  </h3>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDeleteCampaign(selectedCampaign)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-black hover:text-black text-4xl font-bold leading-none"
                  >
                    ×
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Media
                    </label>
                    <div className="flex gap-2 mb-3">
                      <button
                        type="button"
                        onClick={() => setActiveMediaTab("image")}
                        className={`px-4 py-2 rounded transition ${
                          activeMediaTab === "image"
                            ? "bg-purple-600 text-white"
                            : "bg-gray-200"
                        }`}
                      >
                        📷 Image
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveMediaTab("video")}
                        className={`px-4 py-2 rounded transition ${
                          activeMediaTab === "video"
                            ? "bg-purple-600 text-white"
                            : "bg-gray-200"
                        }`}
                      >
                        🎥 Video
                      </button>
                    </div>
                    {activeMediaTab === "image" && (
                      <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer"
                        onClick={() => imageInputRef.current?.click()}
                      >
                        {imagePreview ? (
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded"
                          />
                        ) : (
                          <p className="text-gray-500">Click to upload image</p>
                        )}
                        <input
                          ref={imageInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleImageUpload}
                        />
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded"
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({ ...formData, title: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Notes
                    </label>
                    <textarea
                      rows={3}
                      className="w-full p-2 border rounded"
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData({ ...formData, notes: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Description
                    </label>
                    <textarea
                      rows={2}
                      className="w-full p-2 border rounded"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="space-y-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Schedule</h3>
                    <input
                      type="date"
                      min={getLocalToday()}
                      className="w-full p-2 border rounded mb-2"
                      value={formData.startDate}
                      onChange={(e) =>
                        setFormData({ ...formData, startDate: e.target.value })
                      }
                    />
                    <input
                      type="date"
                      min={formData.startDate || getLocalToday()}
                      className="w-full p-2 border rounded"
                      value={formData.endDate}
                      onChange={(e) =>
                        setFormData({ ...formData, endDate: e.target.value })
                      }
                    />
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Status</h3>
                    <select
                      className="w-full p-2 border rounded"
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          status: e.target.value as "Active" | "Archived",
                        })
                      }
                    >
                      <option value="Active">Active</option>
                      <option value="Archived">Archived</option>
                    </select>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3">Tags</h3>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {formData.tags?.map((tag) => (
                        <span
                          key={tag}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-gray-200 text-gray-700 text-sm rounded-full"
                        >
                          #{tag}
                          <button onClick={() => removeTag(tag)}>×</button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && addTag()}
                        className="flex-1 px-3 py-2 border rounded-lg"
                        placeholder="Add tag..."
                      />
                      <button
                        onClick={addTag}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Events Section */}
                    <div className="bg-gray-50 p-4 rounded-lg min-w-0">
                      <div className="flex items-center justify-between mb-3">
                        <button
                          type="button"
                          onClick={() => setShowEventPicker(true)}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 text-sm"
                        >
                          <span className="text-lg">+</span> Add Events
                        </button>
                      </div>
                      <div className="border border-gray-300 rounded-lg p-3 min-h-[48px] max-h-32 overflow-y-auto flex flex-wrap gap-2 items-start bg-white w-full">
                        {(formData.eventIds || []).length === 0 && (
                          <span className="text-gray-400 text-sm">
                            No events linked
                          </span>
                        )}
                        {(formData.eventIds || []).map((id) => {
                          const event = events.find((e) => e.id === id);
                          if (!event) return null;
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center max-w-full gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                            >
                              <span className="truncate max-w-[220px]">
                                {event.eventName}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeSelectedEvent(id)}
                                className="text-purple-500 hover:text-purple-700 shrink-0"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {/* Assets Section */}
                    <div className="bg-gray-50 p-4 rounded-lg min-w-0">
                      <div className="flex items-center justify-between mb-3">
                        <button
                          type="button"
                          onClick={() => {
                            loadCampaignAssets();
                            setShowAssetPicker(true);
                          }}
                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 text-sm"
                        >
                          <span className="text-lg">+</span> Add Assets
                        </button>
                      </div>
                      <div className="border border-gray-300 rounded-lg p-3 min-h-[48px] max-h-32 overflow-y-auto flex flex-wrap gap-2 items-start bg-white w-full">
                        {(formData.assetIds || []).length === 0 && (
                          <span className="text-gray-400 text-sm">
                            No assets linked
                          </span>
                        )}
                        {(formData.assetIds || []).map((id) => {
                          const asset = findAssetById(id);
                          if (!asset) return null;
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center max-w-full gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                            >
                              <span className="truncate max-w-[220px]">
                                {asset.original_filename}
                              </span>
                              <button
                                type="button"
                                onClick={() => removeSelectedAsset(id)}
                                className="text-purple-500 hover:text-purple-700 shrink-0"
                              >
                                ×
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateCampaign}
                disabled={saving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {showAssetPicker && (
        <AssetPickerModal
          assets={campaignAssets}
          assetsLoading={campaignAssetsLoading}
          selectedIds={formData.assetIds || []}
          onToggle={toggleAssetSelection}
          onClose={() => setShowAssetPicker(false)}
          searchQuery={assetSearchQuery}
          setSearchQuery={setAssetSearchQuery}
        />
      )}

      {showEventPicker && (
        <EventPickerModal
          events={events}
          selectedIds={formData.eventIds || []}
          onToggle={toggleEventSelection}
          onClose={() => setShowEventPicker(false)}
          searchQuery={eventSearchQuery}
          setSearchQuery={setEventSearchQuery}
        />
      )}

      {/* Campaign Details Modal */}
      {showDetailsModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">
                  Campaign Details
                </h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-black hover:text-black text-4xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6">
              {selectedCampaign.image && (
                <img
                  src={selectedCampaign.image}
                  alt={selectedCampaign.title}
                  className="w-full h-64 object-cover rounded-lg mb-6"
                />
              )}
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {selectedCampaign.title}
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="font-medium text-gray-600">Start Date:</span>
                  <span className="text-gray-800">
                    {formatDate(selectedCampaign.startDate)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="font-medium text-gray-600">End Date:</span>
                  <span className="text-gray-800">
                    {formatDate(selectedCampaign.endDate)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2 border-b">
                  <span className="font-medium text-gray-600">Status:</span>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${getStatusColor(selectedCampaign.status)}`}
                  >
                    {selectedCampaign.status}
                  </span>
                </div>
                <div className="py-2 border-b">
                  <span className="font-medium text-gray-600">Notes:</span>
                  <p className="text-gray-800 mt-1">{selectedCampaign.notes}</p>
                </div>
                {selectedCampaign.tags && selectedCampaign.tags.length > 0 && (
                  <div className="py-2">
                    <span className="font-medium text-gray-600">Tags:</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedCampaign.tags.map((tag) => (
                        <span key={tag} className="text-xs text-gray-400">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Product Modal */}
      {showProductModal && !showProductAssetPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">
                  {selectedProduct ? "Edit Product" : "Create Product"}
                </h3>
                <button
                  onClick={() => setShowProductModal(false)}
                  className="text-black hover:text-black text-4xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={productFormData.name}
                  onChange={(e) =>
                    setProductFormData({
                      ...productFormData,
                      name: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Launch Date
                </label>
                <input
                  type="date"
                  min={getLocalToday()}
                  className="w-full p-2 border rounded"
                  value={productFormData.launchDate}
                  onChange={(e) =>
                    setProductFormData({
                      ...productFormData,
                      launchDate: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  rows={3}
                  className="w-full p-2 border rounded"
                  value={productFormData.notes}
                  onChange={(e) =>
                    setProductFormData({
                      ...productFormData,
                      notes: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  rows={2}
                  className="w-full p-2 border rounded"
                  value={productFormData.description}
                  onChange={(e) =>
                    setProductFormData({
                      ...productFormData,
                      description: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  className="w-full p-2 border rounded"
                  value={productFormData.status}
                  onChange={(e) =>
                    setProductFormData({
                      ...productFormData,
                      status: e.target.value as "Active" | "Archived",
                    })
                  }
                >
                  <option value="Active">Active</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Tags (comma separated)
                </label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={productFormData.tags?.join(", ")}
                  onChange={(e) =>
                    setProductFormData({
                      ...productFormData,
                      tags: e.target.value.split(",").map((t) => t.trim()),
                    })
                  }
                  placeholder="tag1, tag2, tag3"
                />
              </div>
              <div>
                <div>
                  <button
                    type="button"
                    onClick={() => setShowProductAssetPicker(true)}
                    className="mb-3 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                  >
                    Add Assets
                  </button>

                  <div className="border border-gray-300 rounded-lg p-3 min-h-[48px] flex flex-wrap gap-2">
                    {(productFormData.assetIds || []).length === 0 && (
                      <span className="text-gray-400 text-sm">
                        No assets linked
                      </span>
                    )}

                    {(productFormData.assetIds || []).map((id) => {
                      const asset = assets.find((a) => a.id === id);

                      if (!asset) return null;

                      return (
                        <span
                          key={id}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                        >
                          {asset.original_filename}

                          <button
                            type="button"
                            onClick={() => removeSelectedProductAsset(id)}
                            className="text-purple-500 hover:text-purple-700"
                          >
                            ×
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => setShowProductModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={
                  selectedProduct ? handleUpdateProduct : handleCreateProduct
                }
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                {selectedProduct ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showProductAssetPicker && (
        <AssetPickerModal
          assets={assets}
          assetsLoading={assetsLoading}
          selectedIds={productFormData.assetIds || []}
          onToggle={toggleProductAssetSelection}
          onClose={() => setShowProductAssetPicker(false)}
          searchQuery={productAssetSearchQuery}
          setSearchQuery={setProductAssetSearchQuery}
        />
      )}

      {/* Content Modal */}
      {showContentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">
                  {selectedContent ? "Edit Content" : "Create Content"}
                </h3>
                <button
                  onClick={() => setShowContentModal(false)}
                  className="text-black hover:text-black text-4xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={contentFormData.name}
                  onChange={(e) =>
                    setContentFormData({
                      ...contentFormData,
                      name: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Date of Creation
                </label>
                <input
                  type="date"
                  className="w-full p-2 border rounded"
                  value={contentFormData.dateOfCreation}
                  onChange={(e) =>
                    setContentFormData({
                      ...contentFormData,
                      dateOfCreation: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  rows={3}
                  className="w-full p-2 border rounded"
                  value={contentFormData.notes}
                  onChange={(e) =>
                    setContentFormData({
                      ...contentFormData,
                      notes: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  className="w-full p-2 border rounded"
                  value={contentFormData.status}
                  onChange={(e) =>
                    setContentFormData({
                      ...contentFormData,
                      status: e.target.value as "Active" | "Archived",
                    })
                  }
                >
                  <option value="Active">Active</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Tags (comma separated)
                </label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={contentFormData.tags?.join(", ")}
                  onChange={(e) =>
                    setContentFormData({
                      ...contentFormData,
                      tags: e.target.value.split(",").map((t) => t.trim()),
                    })
                  }
                  placeholder="tag1, tag2, tag3"
                />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => setShowContentModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={
                  selectedContent ? handleUpdateContent : handleCreateContent
                }
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                {selectedContent ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Modal */}
      {showEventModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">
                  {selectedEvent ? "Edit Event" : "Create Event"}
                </h3>
                <button
                  onClick={() => setShowEventModal(false)}
                  className="text-black hover:text-black text-4xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Event Name *
                </label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={eventFormData.eventName}
                  onChange={(e) =>
                    setEventFormData({
                      ...eventFormData,
                      eventName: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Event Date
                </label>
                <input
                  type="date"
                  min={getLocalToday()}
                  className="w-full p-2 border rounded"
                  value={eventFormData.eventDate}
                  onChange={(e) =>
                    setEventFormData({
                      ...eventFormData,
                      eventDate: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  min={eventFormData.eventDate || getLocalToday()}
                  className="w-full p-2 border rounded"
                  value={eventFormData.endDate}
                  onChange={(e) =>
                    setEventFormData({
                      ...eventFormData,
                      endDate: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  rows={2}
                  className="w-full p-2 border rounded"
                  value={eventFormData.description}
                  onChange={(e) =>
                    setEventFormData({
                      ...eventFormData,
                      description: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea
                  rows={3}
                  className="w-full p-2 border rounded"
                  value={eventFormData.notes}
                  onChange={(e) =>
                    setEventFormData({
                      ...eventFormData,
                      notes: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  className="w-full p-2 border rounded"
                  value={eventFormData.status}
                  onChange={(e) =>
                    setEventFormData({
                      ...eventFormData,
                      status: e.target.value as Event["status"],
                    })
                  }
                >
                  <option value="Scheduled">Scheduled</option>
                  <option value="Active">Active</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Tags (comma separated)
                </label>
                <input
                  type="text"
                  className="w-full p-2 border rounded"
                  value={eventFormData.tags?.join(", ")}
                  onChange={(e) =>
                    setEventFormData({
                      ...eventFormData,
                      tags: e.target.value.split(",").map((t) => t.trim()),
                    })
                  }
                  placeholder="tag1, tag2, tag3"
                />
              </div>
            </div>
            <div className="sticky bottom-0 bg-white px-6 py-4 border-t border-gray-200 flex gap-3 justify-end">
              <button
                onClick={() => setShowEventModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={selectedEvent ? handleUpdateEvent : handleCreateEvent}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                {selectedEvent ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Product Details Modal */}
      {showProductDetailsModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">
                  Product Details
                </h3>
                <button
                  onClick={() => setShowProductDetailsModal(false)}
                  className="text-black hover:text-black text-4xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6">
              {selectedProduct.image && (
                <img
                  src={selectedProduct.image}
                  alt={selectedProduct.name}
                  className="w-full h-64 object-cover rounded-lg mb-6"
                />
              )}
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {selectedProduct.name}
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium">Launch Date:</span>
                  <span>{selectedProduct.launchDate}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium">Status:</span>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${getStatusColor(selectedProduct.status)}`}
                  >
                    {selectedProduct.status}
                  </span>
                </div>
                <div className="py-2 border-b">
                  <span className="font-medium">Notes:</span>
                  <p className="mt-1">{selectedProduct.notes}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Details Modal */}
      {showContentDetailsModal && selectedContent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">
                  Content Details
                </h3>
                <button
                  onClick={() => setShowContentDetailsModal(false)}
                  className="text-black hover:text-black text-4xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6">
              {selectedContent.image && (
                <img
                  src={selectedContent.image}
                  alt={selectedContent.name}
                  className="w-full h-64 object-cover rounded-lg mb-6"
                />
              )}
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {selectedContent.name}
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium">Date of Creation:</span>
                  <span>{selectedContent.dateOfCreation}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium">Status:</span>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${getStatusColor(selectedContent.status)}`}
                  >
                    {selectedContent.status}
                  </span>
                </div>
                <div className="py-2 border-b">
                  <span className="font-medium">Notes:</span>
                  <p className="mt-1">{selectedContent.notes}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Event Details Modal */}
      {showEventDetailsModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-800">
                  Event Details
                </h3>
                <button
                  onClick={() => setShowEventDetailsModal(false)}
                  className="text-black hover:text-black text-4xl font-bold leading-none"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="p-6">
              {selectedEvent.image && (
                <img
                  src={selectedEvent.image}
                  alt={selectedEvent.eventName}
                  className="w-full h-64 object-cover rounded-lg mb-6"
                />
              )}
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {selectedEvent.eventName}
              </h2>
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium">Event Date:</span>
                  <span>{selectedEvent.eventDate}</span>
                </div>
                <div className="flex justify-between py-2 border-b">
                  <span className="font-medium">Status:</span>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${getStatusColor(selectedEvent.status)}`}
                  >
                    {selectedEvent.status}
                  </span>
                </div>
                <div className="py-2 border-b">
                  <span className="font-medium">Notes:</span>
                  <p className="mt-1">{selectedEvent.notes}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
        theme="colored"
      />
    </div>
  );
};

export default CampaignsAndEvents;
